import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useUserProfile } from "../../../hooks/useUserProfile";
import { summarizeTableSituation } from "../../../lib/summarize-table-situation";
import { vibeTypeToText } from "../../../lib/vibe-type-to-text";
import { Place } from "../../../types/Place";
import { Headings } from "../../Headings";
import { LabeledValue } from "../../LabeledValue/LabeledValue";
import PlaceLikes from "../../PlaceLikes/PlaceLikes";
import PlaceStatusAlert from "../../PlaceStatusAlert/PlaceStatusAlert";
import { PublicUserProfileLink } from "../../PublicUserProfileLink/PublicUserProfileLink";
import StaticMap from "../../StaticMap/StaticMap";
import "./PlacePagePlaceInfo.scss";

interface PlacePagePlaceInfoProps {
  place: Place;
}

const PlacePagePlaceInfo: React.FC<PlacePagePlaceInfoProps> = ({ place }) => {
  const { profile: vibeAuthor } = useUserProfile(place.vibeSummary?.author);

  const vibeSummary = useMemo(() => {
    if (!place.vibeSummary?.text) {
      return null;
    }

    const summary = (
      <div className="vibe-summary">
        <div className="vibe-summary-text">{place.vibeSummary.text}</div>
        {vibeAuthor && (
          <PublicUserProfileLink profile={vibeAuthor} size="sm" linkToProfile />
        )}
      </div>
    );

    return <LabeledValue label="Vibe Summary" value={summary} />;
  }, [place.vibeSummary, vibeAuthor]);

  return (
    <div className="place-page-place-info">
      <Headings.PhoneMain>{place.name}</Headings.PhoneMain>
      <PlaceStatusAlert status={place.status} />
      <PlaceLikes place={place} />
      <StaticMap
        lat={place.lat}
        lng={place.lng}
        className="place-map"
        clickToOpenInGoogleMaps
      />
      <div className="place-address">
        <div>{place.address}</div>
        <Link to={`/map#${place.googlePlaceId}`}>View on PoolTonight map</Link>
      </div>

      <div className="place-page-metadata">
        {place.website && (
          <LabeledValue label="Website" value={place.website} />
        )}
        {place.vibes && (
          <LabeledValue
            label="Vibes"
            value={place.vibes.map(vibeTypeToText).join(", ")}
          />
        )}
        {vibeSummary}
        <LabeledValue
          label="Tables"
          value={
            <div>
              {summarizeTableSituation(place.numberOfTables)}{" "}
              {place.paymentTypes && place.paymentTypes.length > 0 && (
                <>&middot; {place.paymentTypes?.join(", ")}</>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};

export default PlacePagePlaceInfo;
